/**
 * 权益兑现数据转换工具函数
 */

/**
 * 解析 realizationFiles JSON 字符串
 * @param {string|null|undefined} jsonStr - 兑现文件 JSON 字符串
 * @returns {{images: string[], experts: Array<{id: number, name: string}>} | null} 解析后的对象，失败返回 null
 */
export function parseRealizationFiles(jsonStr) {
    if (!jsonStr || typeof jsonStr !== 'string') {
        return null;
    }

    try {
        const parsed = JSON.parse(jsonStr);
        return {
            images: Array.isArray(parsed.images) ? parsed.images : [],
            experts: Array.isArray(parsed.experts)
                ? parsed.experts
                      .filter(expert => expert && expert.id != null)
                      .map(expert => ({
                          id: Number(expert.id),
                          name: expert.name || '',
                      }))
                : [],
        };
    } catch (error) {
        console.warn('Failed to parse realizationFiles:', jsonStr, error);
        return null;
    }
}

/**
 * 序列化兑现数据为 JSON 字符串
 * @param {{images?: string[], experts?: Array<{id: number, name: string}>}} data - 兑现数据对象
 * @returns {string} JSON 字符串
 */
export function stringifyRealizationFiles(data) {
    const normalized = {
        images: Array.isArray(data?.images) ? data.images : [],
        experts: Array.isArray(data?.experts)
            ? data.experts.map(expert => ({
                  id: expert.id,
                  name: expert.name || '',
              }))
            : [],
    };
    return JSON.stringify(normalized);
}

/**
 * 统计已兑现权益数量（基于接口原始数据）
 * @param {Array} benefitList - 权益列表
 * @returns {number} 已兑现数量（有图片即已兑现）
 */
export function computeRealizedCount(benefitList) {
    if (!Array.isArray(benefitList)) {
        return 0;
    }

    return benefitList.filter(benefit => {
        const rf = parseRealizationFiles(benefit.realizationFiles);
        return rf?.images?.length > 0;
    }).length;
}

/**
 * 根据当前编辑状态统计已兑现权益数量（实时）
 * 仅依据 images 是否有值，与专家关联无关
 * @param {Object} benefitStates - 权益状态 Map
 * @returns {number} 已兑现数量
 */
export function computeRealizedCountFromStates(benefitStates) {
    if (!benefitStates || typeof benefitStates !== 'object') {
        return 0;
    }

    return Object.values(benefitStates).filter(state => (state?.images?.length ?? 0) > 0).length;
}

/**
 * 初始化权益兑现状态
 * @param {Array} benefitList - 权益列表
 * @returns {Object} 权益状态 Map { [id]: { benefitId, images, experts, remark, changed } }
 */
export function initBenefitStates(benefitList) {
    if (!Array.isArray(benefitList)) {
        return {};
    }

    const states = {};
    for (const benefit of benefitList) {
        const rf = parseRealizationFiles(benefit.realizationFiles);
        states[benefit.id] = {
            benefitId: benefit.benefitId,
            images: rf?.images || [],
            experts: rf?.experts || [],
            remark: benefit.remark || '',
            changed: false,
        };
    }
    return states;
}
